import { z } from "zod";
import { ERROR_MESSAGES } from "../utils/errorMessages";
import type { PropertyFormData, StepId } from "../types";

// Step 1: Property Type
export const propertyTypeSchema = z.object({
  propertyType: z.string().min(1, ERROR_MESSAGES.PROPERTY_TYPE_REQUIRED),
});

// Step 2: Sub Property Type
export const subPropertyTypeSchema = z.object({
  subPropertyType: z.string().min(1, ERROR_MESSAGES.SUB_PROPERTY_TYPE_REQUIRED),
});

// Step 3: Property Size
// This schema needs to be created dynamically based on property type
const createPropertySizeSchema = (propertyType?: string) => {
  const baseSchema = z.object({
    plotSize: z.string().optional(),
    livingSpace: z.string().optional(),
  });

  // For Wohnung: livingSpace is required
  if (propertyType === "Wohnung") {
    return baseSchema.refine(
      (data) => data.livingSpace && data.livingSpace.trim() !== "",
      {
        message: ERROR_MESSAGES.LIVING_SPACE_REQUIRED,
        path: ["livingSpace"],
      }
    );
  }

  // For other types (Haus, Gewerbe, Grundstück): plotSize is required
  return baseSchema.refine(
    (data) => data.plotSize && data.plotSize.trim() !== "",
    {
      message: ERROR_MESSAGES.PLOT_SIZE_REQUIRED,
      path: ["plotSize"],
    }
  );
};

// Step 4: Additional Size Details
// This schema needs to be created dynamically based on property type
const createAdditionalSizeSchema = (propertyType?: string) => {
  const baseSchema = z.object({
    rooms: z.string().optional(),
    livingSpace: z.string().optional(),
    commercialArea: z.string().optional(),
  });

  // For Wohnung: rooms is required
  if (propertyType === "Wohnung") {
    return baseSchema.refine((data) => data.rooms && data.rooms.trim() !== "", {
      message: ERROR_MESSAGES.ROOMS_REQUIRED,
      path: ["rooms"],
    });
  }

  // For Haus: livingSpace is required
  if (propertyType === "Haus") {
    return baseSchema.refine(
      (data) => data.livingSpace && data.livingSpace.trim() !== "",
      {
        message: ERROR_MESSAGES.LIVING_SPACE_REQUIRED,
        path: ["livingSpace"],
      }
    );
  }

  // For Gewerbe: commercialArea is required
  if (propertyType === "Gewerbe") {
    return baseSchema.refine(
      (data) => data.commercialArea && data.commercialArea.trim() !== "",
      {
        message: ERROR_MESSAGES.COMMERCIAL_AREA_REQUIRED,
        path: ["commercialArea"],
      }
    );
  }

  // For Grundstück: this step is skipped, but return a valid schema
  return baseSchema;
};

// Step 5: Construction Year
export const constructionYearSchema = z.object({
  constructionYear: z
    .string()
    .min(1, ERROR_MESSAGES.CONSTRUCTION_YEAR_REQUIRED)
    .regex(/^\d{4}$/, "Bitte geben Sie ein gültiges Jahr ein (z.B. 1996)"),
});

// Step 6: Property Features (checkboxes - optional)
export const propertyFeaturesSchema = z.object({
  propertyFeatures: z.array(z.string()).optional(),
});

// Step 7: Quality
export const qualitySchema = z.object({
  quality: z.string().min(1, ERROR_MESSAGES.QUALITY_REQUIRED),
});

// Step 8: Condition
export const conditionSchema = z.object({
  condition: z.string().min(1, ERROR_MESSAGES.CONDITION_REQUIRED),
});

// Step 9: Usage
export const usageSchema = z.object({
  usage: z.string().min(1, "Bitte wählen Sie die Nutzung aus"),
});

// Step 10: Purpose
const basePurposeSchema = z.object({
  reason: z.string().min(1, ERROR_MESSAGES.REASON_REQUIRED),
});

// Step 11: Timeline (conditional on purpose)
const purposeWithTimelineSchema = basePurposeSchema.extend({
  timeline: z.string().min(1, ERROR_MESSAGES.TIMELINE_REQUIRED),
});

// Step 12: Address
export const addressSchema = z.object({
  city: z.string().min(1, ERROR_MESSAGES.CITY_REQUIRED),
  street: z.string().min(1, ERROR_MESSAGES.STREET_REQUIRED),
  house_number: z.string().min(1, ERROR_MESSAGES.HOUSE_NUMBER_REQUIRED),
  zip_code: z.string().min(1, ERROR_MESSAGES.ZIP_CODE_REQUIRED),
});

// Step 13: Contact
export const contactSchema = z.object({
  first_name: z.string().min(1, ERROR_MESSAGES.FIRST_NAME_REQUIRED),
  last_name: z.string().min(1, ERROR_MESSAGES.LAST_NAME_REQUIRED),
  email: z.string().email(ERROR_MESSAGES.EMAIL_INVALID),
  home_phone: z.string().min(1, ERROR_MESSAGES.PHONE_REQUIRED),
  accept_contact: z.boolean().refine((val) => val === true, {
    message: ERROR_MESSAGES.ACCEPT_CONTACT_REQUIRED,
  }),
});

/**
 * Get the validation schema for a specific step based on form data
 */
export function getStepSchema(
  stepId: StepId,
  formData: Partial<PropertyFormData>
): z.ZodSchema {
  switch (stepId) {
    case "property-type":
      return propertyTypeSchema;

    case "sub-type":
      return subPropertyTypeSchema;

    case "property-size":
      return createPropertySizeSchema(formData.propertyType);

    case "additional-size":
      return createAdditionalSizeSchema(formData.propertyType);

    case "construction-year":
      if (formData.propertyType === "Grundstück") {
        return z.object({}); // Skip for Grundstück
      }
      return constructionYearSchema;

    case "property-features":
      if (formData.propertyType === "Grundstück") {
        return z.object({}); // Skip for Grundstück
      }
      return propertyFeaturesSchema;

    case "quality":
      if (formData.propertyType === "Grundstück") {
        return z.object({}); // Skip for Grundstück
      }
      return qualitySchema;

    case "condition":
      if (formData.propertyType === "Grundstück") {
        return z.object({}); // Skip for Grundstück
      }
      return conditionSchema;

    case "usage":
      if (formData.propertyType === "Grundstück") {
        return z.object({}); // Skip for Grundstück
      }
      return usageSchema;

    case "purpose":
      return basePurposeSchema;

    case "property-search":
      // Always fail validation to prevent navigation forward
      // User must go back to complete the evaluation
      return z
        .object({
          propertySearchAction: z.string().optional().default(""),
        })
        .superRefine((_data, ctx) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'Für Kaufinteressenten können wir leider keine Bewertung bereitstellen. Bitte klicken Sie auf "Jetzt Immobilie finden", um passende Angebote zu finden.',
            path: ["propertySearchAction"],
          });
        });

    case "timeline":
      if (
        formData.reason === "Verkaufsabsicht" ||
        formData.reason === "Kaufabsicht"
      ) {
        return purposeWithTimelineSchema;
      }
      return z.object({}); // Skip if not needed

    case "address":
      return addressSchema;

    case "contact":
      return contactSchema;

    default:
      return z.object({});
  }
}
